import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
    FaHashtag, FaThumbsUp, FaUserFriends, FaCameraRetro, FaVideo, FaBullhorn,
    FaCheckCircle, FaChevronDown, FaChevronUp, FaChevronRight, FaRegHeart, FaRetweet,
    FaSearch, FaChartLine,
    FaInstagram, FaFacebook, FaLinkedin, FaYoutube, FaPinterest, FaTwitter, FaSnapchatGhost, FaTiktok
} from 'react-icons/fa';
import heroImage from '../../assets/download (1).png';

const SocialMediaPage = () => {
    const [openFaq, setOpenFaq] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaq(openFaq === index ? null : index);
    };

    const services = [
        { icon: FaHashtag, title: 'Social Strategy', desc: 'Crafting a unique voice and content pillars that resonate with your target audience.', image: 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=600' },
        { icon: FaCameraRetro, title: 'Content Creation', desc: 'Producing scroll-stopping reels, posts, and stories with high-end visuals.', image: 'https://images.unsplash.com/photo-1611162616475-46b635cb6868?w=600' },
        { icon: FaUserFriends, title: 'Community Mgmt', desc: ' engaging with your followers, replying to comments, and building brand loyalty.', image: 'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600' },
        { icon: FaBullhorn, title: 'Influencer Marketing', desc: 'Collaborating with niche influencers to amplify your reach authentically.', image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=600' },
        { icon: FaVideo, title: 'Video Production', desc: 'Professional editing for TikTok, Reels, and YouTube Shorts that go viral.', image: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=600' },
        { icon: FaCheckCircle, title: 'Brand Reputation', desc: 'Monitoring brand sentiment and managing crisis communication effectively.', image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=600' }
    ];

    const platforms = [
        { name: 'Instagram', icon: FaInstagram, desc: 'Visual Storytelling' },
        { name: 'Facebook', icon: FaFacebook, desc: 'Community Building' },
        { name: 'LinkedIn', icon: FaLinkedin, desc: 'B2B Networking' },
        { name: 'TikTok', icon: FaTiktok, desc: 'Viral Short Video' }, // Note: If FaTiktok fails, I will use FaMusic
        { name: 'YouTube', icon: FaYoutube, desc: 'Long-form Content' },
        { name: 'Twitter / X', icon: FaTwitter, desc: 'Real-time Updates' },
        { name: 'Pinterest', icon: FaPinterest, desc: 'Inspiration & Shop' },
        { name: 'Snapchat', icon: FaSnapchatGhost, desc: 'Gen Z Engagement' }
    ];

    // Horizontal Flow Steps (Social Growth)
    const flowSteps = [
        { title: 'Audit', icon: FaSearch, color: 'from-pink-500 to-rose-500' },
        { title: 'Plan', icon: FaRegHeart, color: 'from-purple-500 to-indigo-500' },
        { title: 'Create', icon: FaCameraRetro, color: 'from-blue-500 to-cyan-500' },
        { title: 'Post', icon: FaRetweet, color: 'from-green-500 to-emerald-500' },
        { title: 'Engage', icon: FaUserFriends, color: 'from-yellow-400 to-orange-500' },
        { title: 'Grow', icon: FaChartLine, color: 'from-red-500 to-pink-600' }
    ];

    const faqs = [
        { q: 'Which platform is best for my business?', a: 'It depends on your goals. LinkedIn is great for B2B, while Instagram and TikTok are essential for B2C lifestyle brands.' },
        { q: 'How often should I post?', a: 'Consistency is key. We recommend 3-5 times per week for feed posts and daily for Stories/Reels.' },
        { q: 'Do buy followers works?', a: 'Never. Bought followers hurt your engagement rate and credibility. We focus on organic, authentic growth.' },
        { q: 'Can you handle customer support on social?', a: 'Yes, our community managers can reply to DMs and comments, escalating support issues to your team as needed.' },
        { q: 'Do you work with influencers?', a: 'Yes, we manage the entire influencer relationship from outreach and negotiation to campaign execution.' }
    ];

    const whyChooseUs = [
        { title: 'Viral Content Strategy', desc: 'We know what makes people stop scrolling and start sharing.' },
        { title: 'Authentic Engagement', desc: 'We help you build a real community, not just a follower count.' },
        { title: 'Cross-Platform Expertise', desc: 'Unified messaging tailored to the unique culture of each platform.' },
        { title: 'Creative Studio', desc: 'In-house designers and editors to make your brand look world-class.' },
        { title: 'Trend Spotting', desc: 'We jump on audio and challenge trends before they peak.' },
        { title: 'Performance Tracking', desc: 'We measure Reach, Impressions, Engagement Rate, and Click-Throughs.' }
    ];

    return (
        <div className="bg-white font-sans">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-gradient-to-r from-pink-900/90 via-purple-900/80 to-transparent"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, x: -50 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-2xl text-white"
                    >
                        <p className="text-xs font-light tracking-widest uppercase text-pink-300 mb-3">What We Do &gt; Engagement</p>
                        <h1 className="text-4xl md:text-5xl font-light mb-4 leading-tight">Social Media Marketing</h1>
                        <p className="text-base font-light text-gray-200 leading-relaxed mb-6">
                            Turn followers into fans and fans into customers with high-impact social strategies.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a href="#inquiry" className="px-5 py-2 bg-white text-pink-700 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                                Get Social Audit
                            </a>
                            <a href="#services" className="px-5 py-2 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-pink-700 transition-all text-sm whitespace-nowrap">
                                View Portfolio
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Intro Stats */}
            <section className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="max-w-4xl mx-auto text-center mb-16">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Social Impact</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            Building brands clearly, loudly, and authentically.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-8 text-center divide-x divide-slate-200">
                        {[
                            { label: 'Followers Gained', val: '2M+' },
                            { label: 'Viral Impressions', val: '500M+' },
                            { label: 'Engagement Rate', val: '5.8%' },
                            { label: 'Brands Scaled', val: '120+' }
                        ].map((stat, idx) => (
                            <div key={idx} className="p-4">
                                <div className="text-4xl font-bold text-pink-600 mb-2">{stat.val}</div>
                                <div className="text-sm text-slate-500 uppercase tracking-wider">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Technologies / Platforms */}
            <section className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Platforms We Master</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            Optimizing your presence where your audience lives.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-4 gap-6">
                        {platforms.map((plat, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.05 }}
                                className="p-6 bg-slate-50 rounded-sm border border-slate-200 text-center hover:shadow-lg transition-all group"
                            >
                                <plat.icon className="text-5xl mx-auto mb-4 text-pink-600 group-hover:scale-110 transition-transform" />
                                <h3 className="text-lg font-medium text-slate-900 mb-2">{plat.name}</h3>
                                <p className="text-sm text-slate-600">{plat.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Grid (Hover Cards) */}
            <section id="services" className="py-20 bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light text-slate-900 mb-6">Social Services</h2>
                        <p className="text-lg text-slate-600 max-w-3xl mx-auto">
                            From content creation to community management.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {services.map((service, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="group relative h-64 overflow-hidden rounded-sm border border-slate-200 cursor-pointer"
                            >
                                <div className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110" style={{ backgroundImage: `url(${service.image})` }}>
                                    <div className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/80"></div>
                                </div>
                                <div className="absolute inset-0 p-6 flex flex-col justify-between text-white z-10">
                                    <div>
                                        <service.icon className="text-4xl mb-4 text-pink-300" />
                                        <h3 className="text-xl font-medium mb-2">{service.title}</h3>
                                    </div>
                                    <motion.div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-pink-600 to-purple-600 p-6 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500">
                                        <p className="text-white text-sm leading-relaxed">{service.desc}</p>
                                        <button className="mt-4 text-white font-medium hover:underline">Learn More →</button>
                                    </motion.div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Why Choose Us */}
            <section className="py-20 bg-gradient-to-br from-pink-600 via-purple-600 to-indigo-700 text-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-12">
                        <h2 className="text-4xl font-light mb-6">Why We Go Viral</h2>
                        <p className="text-lg text-pink-100 max-w-3xl mx-auto">
                            Strategic creativity that captures attention.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {whyChooseUs.map((item, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="bg-white/10 backdrop-blur-sm p-6 rounded-sm border border-white/20 hover:bg-white/20 transition-all"
                            >
                                <FaCheckCircle className="text-3xl text-pink-300 mb-4" />
                                <h3 className="text-xl font-medium mb-3">{item.title}</h3>
                                <p className="text-pink-100 leading-relaxed">{item.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Timeline (Campaign Flow with Arrows) */}
            <section className="py-24 bg-slate-50 border-y border-slate-200">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-20">
                        <h2 className="text-3xl md:text-4xl font-light text-slate-900 mb-4">The Viral Formula</h2>
                        <p className="text-slate-600">How we build your social empire.</p>
                    </div>

                    <div className="relative">
                        {/* Base Line (Grey) */}
                        <div className="hidden md:block absolute top-[40px] left-0 w-full h-1 bg-slate-200 md:rounded-full -z-0"></div>

                        {/* Steps Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-6 gap-0 relative">
                            {flowSteps.map((step, idx) => (
                                <div key={idx} className="relative flex flex-col items-center group">

                                    {/* ARROW: Only show BETWEEN steps */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="hidden md:flex absolute top-[30px] -right-[50%] w-full justify-center items-center z-10 pointer-events-none">
                                            <motion.div
                                                animate={{ x: [-3, 3, -3] }}
                                                transition={{ repeat: Infinity, duration: 1.5, ease: "easeInOut" }}
                                            >
                                                <FaChevronRight className="text-2xl text-slate-400 font-bold" />
                                            </motion.div>
                                        </div>
                                    )}

                                    {/* Circle Icon */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5, y: 20 }}
                                        whileInView={{ opacity: 1, scale: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.1, type: "spring", stiffness: 100 }}
                                        className="relative z-20"
                                    >
                                        <div className={`w-20 h-20 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center text-white text-3xl shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300 border-4 border-white ring-4 ring-slate-50`}>
                                            <step.icon />
                                        </div>
                                    </motion.div>

                                    {/* Number Badge */}
                                    <div className="absolute top-0 right-1/2 translate-x-10 -translate-y-0 w-7 h-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center border-2 border-white shadow-md z-30">
                                        {idx + 1}
                                    </div>

                                    {/* Title */}
                                    <h3 className="text-lg font-bold text-slate-800 group-hover:text-pink-600 transition-colors mt-2">{step.title}</h3>

                                    {/* Mobile Vertical Line */}
                                    {idx < flowSteps.length - 1 && (
                                        <div className="md:hidden absolute bottom-[-40px] left-1/2 w-0.5 h-[40px] bg-slate-300"></div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FAQ & Inquiry Form */}
            <section id="inquiry" className="py-20 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12">
                    <div className="grid lg:grid-cols-2 gap-12">

                        {/* FAQs */}
                        <div>
                            <h2 className="text-3xl font-light text-slate-900 mb-8">Social FAQs</h2>
                            <div className="space-y-4">
                                {faqs.map((faq, idx) => (
                                    <motion.div
                                        key={idx}
                                        initial={{ opacity: 0, y: 10 }}
                                        whileInView={{ opacity: 1, y: 0 }}
                                        viewport={{ once: true }}
                                        transition={{ delay: idx * 0.05 }}
                                        className="border border-slate-200 rounded-sm overflow-hidden"
                                    >
                                        <button
                                            onClick={() => toggleFaq(idx)}
                                            className="w-full px-6 py-4 text-left flex justify-between items-center bg-slate-50 hover:bg-slate-100 transition-colors"
                                        >
                                            <span className="font-medium text-slate-900">{faq.q}</span>
                                            {openFaq === idx ? <FaChevronUp className="text-pink-600" /> : <FaChevronDown className="text-slate-400" />}
                                        </button>
                                        {openFaq === idx && (
                                            <div className="px-6 py-4 bg-white">
                                                <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                            </div>
                        </div>

                        {/* Inquiry Form */}
                        <div className="bg-slate-50 p-8 rounded-sm border border-slate-200">
                            <h2 className="text-3xl font-light text-slate-900 mb-2">Start Your Social Growth</h2>
                            <p className="text-slate-600 mb-6">Let's consistently reach your audience.</p>

                            <form className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Handle / Brand Name</label>
                                    <input type="text" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-pink-600" placeholder="@YourBrand" />
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                                        <input type="email" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-pink-600" placeholder="john@example.com" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
                                        <input type="tel" className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-pink-600" placeholder="+91 98765 43210" />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-700 mb-1">Primary Platform</label>
                                    <select className="w-full px-4 py-3 border border-slate-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-pink-600">
                                        <option>Instagram</option>
                                        <option>LinkedIn</option>
                                        <option>TikTok</option>
                                        <option>YouTube</option>
                                    </select>
                                </div>

                                <button type="submit" className="w-full bg-pink-600 text-white font-medium py-3 rounded-sm hover:bg-pink-700 transition-colors shadow-lg">
                                    Book Strategy Call
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="py-20 bg-gradient-to-r from-pink-700 to-purple-800 text-white">
                <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
                    <h2 className="text-4xl md:text-5xl font-light mb-6">Want to go Viral?</h2>
                    <p className="text-xl font-light mb-8 text-pink-100">
                        Stop posting into the void. Start building a community.
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                        <a href="#inquiry" className="inline-block px-6 py-2.5 bg-white text-pink-800 font-medium rounded-sm hover:shadow-xl transition-all text-sm whitespace-nowrap">
                            Get Quote
                        </a>
                        <a href="tel:+919876543210" className="inline-block px-6 py-2.5 border-2 border-white text-white font-medium rounded-sm hover:bg-white hover:text-pink-800 transition-all text-sm whitespace-nowrap">
                            Contact Us
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default SocialMediaPage;
